/**
 * One section in the editor: its draft (text, audience, a custom section's title), **Save** (one `PUT` under the
 * revision rule; warnings in the answer stay under the text) and, for a saved section, **Remove** after a
 * confirmation. A new custom section takes its key from its title on the first save.
 */

import { useState } from 'react';
import { Save, Trash2 } from 'lucide-react';

import { SectionIn, sectionInBodyMax } from '@/api/generated/schemas';
import { isStaleRevision, useRemoveSection, useSaveSection } from '@/api/queries';
import type { AssetOut, SectionAudience } from '@/api/types';
import { fieldErrors, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState, type ButtonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { SectionCard } from '@/ui/SectionCard';

import { customKey, type PlannedSection } from './sectionPlan';

/** What the author types; the revision, audience and position come from the editor. */
const TYPED = SectionIn.pick({ body: true, title: true });

function startingAudience(planned: PlannedSection): SectionAudience {
  if (planned.section) return planned.section.audience as SectionAudience;
  return planned.internal ? 'internal' : 'shareable';
}

export interface SectionEditorCardProps {
  asset: AssetOut;
  planned: PlannedSection;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onStale: () => void;
  /** Warnings from the last save of this key; kept by the list, since a first save moves the card. */
  warnings: string[];
  onWarnings: (key: string, warnings: string[]) => void;
  /** A new custom section was saved (it now shows among the saved ones) or dropped before saving. */
  onDone?: () => void;
}

function CardActions(props: {
  saved: boolean;
  draftCustom: boolean;
  title: string;
  state: ButtonState;
  canSave: boolean;
  onRemove: () => void;
  onDiscard?: () => void;
  onSave: () => void;
}) {
  return (
    <>
      {props.saved && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={props.onRemove}>
          <Trash2 aria-hidden="true" /> Remove
        </button>
      )}
      {props.draftCustom && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={props.onDiscard}>Discard</button>
      )}
      <ActionButton
        className="btn-primary btn-sm"
        state={props.state}
        label={`Save ${props.title.trim() || 'section'}`}
        icon={<Save aria-hidden="true" />}
        disabled={!props.canSave}
        onClick={props.onSave}
      />
    </>
  );
}

function useSectionDraft(planned: PlannedSection) {
  const [body, setBody] = useState(planned.section?.body ?? '');
  const [title, setTitle] = useState(planned.title);
  const [audience, setAudience] = useState<SectionAudience>(startingAudience(planned));
  const saved = planned.section;
  const changed =
    body !== (saved?.body ?? '') || audience !== startingAudience(planned) || (planned.state === 'custom' && title.trim() !== (saved?.title ?? ''));
  return { body, setBody, title, setTitle, audience, setAudience, changed };
}

export function SectionEditorCard(props: SectionEditorCardProps) {
  const { asset, planned, onMoveUp, onMoveDown, onStale, onDone } = props;
  const { can } = useSession();
  const draft = useSectionDraft(planned);
  const save = useSaveSection(asset.id);
  const remove = useRemoveSection(asset.id);
  const [removing, setRemoving] = useState(false);
  const isNew = planned.section === null;
  const custom = planned.state === 'custom';
  const onError = (error: unknown) => isStaleRevision(error) && onStale();
  // A custom section's key comes from its title, so it needs one (an SPA rule on top of the API's).
  const valid = Object.keys(fieldErrors(TYPED, { body: draft.body, title: draft.title })).length === 0 && !(custom && !draft.title.trim());
  const server = serverFieldErrors(save.error);

  const submit = () => {
    const key = custom && isNew ? customKey(draft.title, asset.sections.map((s) => s.key)) : planned.key;
    save.mutate(
      { key, body: { revision: asset.revision, body: draft.body, audience: draft.audience, title: custom ? draft.title.trim() : null } },
      {
        onSuccess: (answer) => {
          props.onWarnings(key, answer.warnings);
          if (custom && isNew) onDone?.();
        },
        onError,
      },
    );
  };

  const actions = (
    <CardActions
      saved={!isNew}
      draftCustom={custom && isNew}
      title={draft.title}
      state={buttonState(save)}
      canSave={draft.changed && valid}
      onRemove={() => setRemoving(true)}
      onDiscard={onDone}
      onSave={submit}
    />
  );

  return (
    <>
      <SectionCard
        title={draft.title}
        state={planned.state}
        onTitle={custom ? draft.setTitle : undefined}
        audience={draft.audience}
        onAudience={draft.setAudience}
        audienceLocked={can('asset.view.internal') ? undefined : 'Only people who may read internal content can change the audience.'}
        body={draft.body}
        onBody={draft.setBody}
        maxLength={sectionInBodyMax}
        errors={{ title: server.title, body: server.body }}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        warnings={props.warnings}
        actions={actions}
      />
      {removing && (
        <ConfirmDialog
          title={`Remove ${planned.title}?`}
          confirmLabel="Remove"
          danger
          pending={remove.isPending}
          error={remove.error}
          onConfirm={() =>
            remove.mutate({ key: planned.key, title: planned.title, revision: asset.revision }, { onSuccess: () => setRemoving(false), onError })
          }
          onClose={() => setRemoving(false)}
        >
          <p>{custom ? 'The section and its text are deleted.' : 'Its text is deleted; the section goes back to the suggestions.'} The history keeps what it said.</p>
        </ConfirmDialog>
      )}
    </>
  );
}
